import Header from './HeaderComponent';
import SessionWatcher from './SessionWatcher';
import React, { Component, Suspense, lazy } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { fetchAllTasks, fetchComments, fetchTimetables } from '../redux/ActionCreators';
import { connect } from 'react-redux';
import { baseUrl, STUDENT_ID } from '../Shared/baseURL';
import LandingPage from "../pages/LandingPage";
import { analyticsToggleFromQuery } from '../analytics/analyticsToggleFromQuery';
import { isSubscriptionEntitled, isEntitlementActive } from '../logic/planSelection';
import {
  clearAuthSessionStorage,
  setStoredUserAnalyticsDisabled,
  syncCurrentUserProfileToStorage,
  getStoredSubscriptions,
  setStoredSubscriptions
} from '../logic/authStorage';
import { getActiveCourseId, setActiveCourseId } from '../logic/courseContext';
import { resolveEntitlementFromProfile } from '../logic/effectiveEntitlement';
import { canViewParentDashboard, STUDENT_HOME_PATH } from '../logic/homeRoute';

import axios from 'axios';
import * as Sentry from '@sentry/react';

// Route components are lazy-loaded so the public landing route does not pull in the full app upfront.
const PracticeTest = lazy(() => import('./TakeTests'));
const QuestionManager = lazy(() => import('./QuestionManager'));
const PromptManager = lazy(() => import('./PromptManager'));
const WritingPromptManager = lazy(() => import('./WritingPromptManager'));
const PatternWizard = lazy(() => import('./PatternWizard'));
const QuestionAuthoringStudio = lazy(() => import('./QuestionAuthoringStudio'));
const BatchQuestionUpload = lazy(() => import('./BatchQuestionUpload'));
const StudentRemediationDashboard = lazy(() => import('./StudentRemediationDashboard'));
const RealExamInstructions = lazy(() => import('./RealExamInstructions'));
const RealExamPlayerPage = lazy(() => import('./RealExamPlayerPage'));
const Finance = lazy(() => import('./finance/finance'));
const LoanRepaymentSchedule = lazy(() => import('./finance/LoanRepaymentSchedule'));
const CustomTestBuilder = lazy(() => import('./CustomTestBuilder'));
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const PatternDetailsReport = lazy(() => import('./PatternDetailsReport'));
const ParentRegister = lazy(() => import('./Parent/ParentRegister'));
const EmailVerificationPage = lazy(() => import('./Parent/EmailVerificationPage'));
const StudentLogin = lazy(() => import('./Login/StudentLogin'));
const LoginPage = lazy(() => import('./Login/ParentLogin'));
const TermsPage = lazy(() => import('../Content/TermsPage'));
const PrivacyPage = lazy(() => import('../Content/PrivacyPage'));
const CheckoutPage = lazy(() => import('./Parent/CheckoutPage'));
const SuccessPage = lazy(() => import('./Parent/SuccessPage'));
const CancelPage = lazy(() => import('./Parent/CancelPage'));
const AddStudentForm = lazy(() => import('./Student/AddStudentForm'));
const ParentDashboard = lazy(() => import('./Parent/ParentDashboard'));
const Results = lazy(() => import('./Results'));
const ForgotPassword = lazy(() => import('./Login/ForgotPassword'));
const ResetPassword = lazy(() => import('./Login/ResetPassword'));
const GettingStarted = lazy(() => import('./Parent/GettingStarted'));
const SessionExpired = lazy(() => import('./SessionExpired'));
const SubscriptionRequired = lazy(() => import('./SubscriptionRequired'));
const TopicsView = lazy(() => import('../pages/TopicsView'));

const mapStateToProps = state => {
  return {
    alltasks: state.alltasks,
    dailylogs: state.dailylogs,
    timetables: state.timetables
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchAllTasks: () => dispatch(fetchAllTasks()),
  fetchTimetables: () => dispatch(fetchTimetables()),
  fetchComments: () => { dispatch(fetchComments()) }
});



class Home extends Component {
  constructor(props) {
    super(props);
    Sentry.init({
      dsn: "https://8d9653bec6f6bac603e9b24cb19337b7@o4509416587591680.ingest.us.sentry.io/4509416602206208",
      tracesSampleRate: 1.0,
    });
    // Rehydrate user from localStorage synchronously
    const token = localStorage.getItem('studyapp.com');
    let initialUser = null;
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      initialUser = {
        token,
        firstname: localStorage.getItem('studyapp.com_firstname'),
        userID: localStorage.getItem('studyapp.com_userID'),
        role: localStorage.getItem('studyapp_role'),
        admin: localStorage.getItem('studyapp_admin') === 'true',
        subscriptions: getStoredSubscriptions()
      };
    }
    const initialSubscription = initialUser?.admin === true
      ? true
      : isSubscriptionEntitled(initialUser?.subscriptions || [], getActiveCourseId());
    this.state = {
      user: initialUser,
      childrenList: [],
      subscriptionActive: initialSubscription,
      loadingSubscriptionCheck: true,
      sessionRemainingMs: null
    };
    this.handleLoginSuccess = this.handleLoginSuccess.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleStudentAdded = this.handleStudentAdded.bind(this);
    this.handleSessionTick = this.handleSessionTick.bind(this);
    this.handleStorageChange = this.handleStorageChange.bind(this);
  }

  // Another tab logged out, or a student signed in over this parent (STUD-896): this
  // tab's cached user and Authorization header are now stale. Reload so it boots into
  // whatever session is stored, through the normal start-up path. Storage events never
  // fire in the tab that made the change, so this only reacts to other tabs.
  handleStorageChange(e) {
    const currentToken = this.state.user?.token ?? null;
    // key === null means localStorage.clear() in another tab.
    const tokenChanged = e.key === 'studyapp.com'
      ? (e.newValue ?? null) !== currentToken
      : e.key === null && currentToken !== null;
    if (tokenChanged) {
      window.location.reload();
    }
  }

  // Stable callback passed to SessionWatcher so its effect doesn't re-run
  // needlessly every render.
  handleSessionTick(remainingMs) {
    this.setState({ sessionRemainingMs: remainingMs });
  }


  handleStudentAdded(student) {
    localStorage.setItem('selectedStudentID', student._id);
    localStorage.setItem('selectedStudent_name', `${student.firstname} `);

    this.setState(state => ({
      childrenList: [...state.childrenList, student]
    }));
  }


  async componentDidMount() {
    // Re-apply query-based analytics override once the routed shell is mounted.
    analyticsToggleFromQuery();

    window.addEventListener('storage', this.handleStorageChange);

    // Refresh cached user flags from server truth so admin-side analytics changes
    // apply again after a reload even when auth is restored from local storage.
    // This single call also settles entitlement for EVERY role -- a student no longer
    // needs a second request for the parent's record.
    await this.refreshCurrentUserProfile();

    Sentry.captureMessage("Home component mounted, checking subscription status");

    this.setState({ loadingSubscriptionCheck: false });

  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.handleStorageChange);
  }

  componentDidUpdate(prevProps) {
    const searchChanged = prevProps.location?.search !== this.props.location?.search;
    const hashChanged = prevProps.location?.hash !== this.props.location?.hash;
    if (!searchChanged && !hashChanged) {
      return;
    }
    analyticsToggleFromQuery();
  }

  refreshCurrentUserProfile = async () => {
    const token = localStorage.getItem('studyapp.com');
    if (!token) {
      return;
    }

    try {
      // Ask about the course in context so the server can apply per-course precedence.
      // Only a canonical id is sent: the endpoint rejects an invalid courseId with a
      // 400 rather than quietly answering account-wide.
      const activeCourseId = getActiveCourseId();
      const response = await axios.get(baseUrl + 'users/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: activeCourseId ? { courseId: activeCourseId } : undefined
      });
      const currentUser = response.data?.user || response.data || {};
      // Syncs role, admin, analyticsDisabled, and subscriptions to localStorage.
      syncCurrentUserProfileToStorage(currentUser);

      const subscriptions = currentUser.subscriptions || [];
      // The server's verdict, which already folds in the admin bypass and -- for a
      // student -- the PARENT's subscriptions, without disclosing them.
      const { subscriptionActive, courseIdToActivate } = resolveEntitlementFromProfile(
        currentUser.effectiveEntitlement,
        activeCourseId
      );
      if (courseIdToActivate) {
        setActiveCourseId(courseIdToActivate);
      }

      this.setState((prevState) => {
        if (!prevState.user) {
          return null;
        }
        return {
          subscriptionActive,
          user: {
            ...prevState.user,
            role: currentUser.role || prevState.user.role,
            admin: typeof currentUser.admin !== 'undefined' ? currentUser.admin : prevState.user.admin,
            analyticsDisabled: typeof currentUser.analyticsDisabled === 'boolean'
              ? currentUser.analyticsDisabled
              : prevState.user.analyticsDisabled,
            subscriptions
          }
        };
      });
    } catch (error) {
      console.error('Failed to refresh current user profile', error);
      Sentry.captureException(error);
      // Fail closed. This is a deliberate change for parents, who previously kept the
      // entitlement cached in localStorage when this call failed -- user-editable state
      // the shell should not trust. The server gate is authoritative regardless, so the
      // cost of a transient failure is a re-prompt, not lost access.
      this.setState({ subscriptionActive: false });
    }
  };

  // Called by LoginPage when login succeeds
  async handleLoginSuccess(data) {
    // persist token & configure axios
    localStorage.setItem('studyapp.com', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    const subscriptions = data.subscriptions || [];
    setStoredSubscriptions(subscriptions);
    setStoredUserAnalyticsDisabled(Boolean(data.analyticsDisabled));
    if (!getActiveCourseId()) {
      const entitled = subscriptions.find(s => isEntitlementActive(s) && s.courseId) || subscriptions.find(s => s.courseId);
      if (entitled?.courseId) setActiveCourseId(entitled.courseId.toString());
    }
    const active = data.isAdmin
      ? true
      : isSubscriptionEntitled(subscriptions, getActiveCourseId());
    // lift user into Home state
    this.setState({
      user: {
        firstname: data.firstname,
        userID: data.userID,
        role: data.role,
        token: data.token,
        admin: data.admin,
        analyticsDisabled: Boolean(data.analyticsDisabled),
        subscriptions
      },
      subscriptionActive: active
    }, () => {
      this.setState({ loadingSubscriptionCheck: false });
    });

    // if parent or teacher, fetch children once
    if (data.role === 'parent' || data.role === 'teacher') {
      try {
        const res = await axios.get(baseUrl + `users?parentId=${data.userID}`);
        if (Array.isArray(res.data) && res.data.length === 0) {
          // no students, redirect to add-student page immediately
          window.location.href = '/add-student';
          return;
        }
        this.setState({ childrenList: res.data });
      } catch (err) {
        console.error('Error fetching children:', err);
        Sentry.captureException(err);
      }
    }
  }

  // Logout handler
  handleLogout() {
    clearAuthSessionStorage();
    delete axios.defaults.headers.common['Authorization'];
    this.setState({ user: null, childrenList: [], subscriptionActive: false });
  }

  render() {
    const { alltasks, dailylogs, timetables } = this.props;
    const isAuthenticated = Boolean(this.state.user && this.state.user.token);
    const isRealExamPlayerRoute = this.props.location?.pathname === '/take-tests/real-exam/play';
    const outerClassName = isRealExamPlayerRoute ? 'real-exam-player-layout' : 'container-fluid';
    const contentClassName = isRealExamPlayerRoute ? 'real-exam-player-content' : 'container-fluid';
    const contentStyle = isRealExamPlayerRoute ? undefined : { paddingTop: '70px' };
    const routeLoadingFallback = <div style={{ padding: '2rem' }}>Loading...</div>;

    if (this.state.loadingSubscriptionCheck) {
      return <div>Checking subscription status...</div>;
    }

    return (
      <div className={outerClassName}>
        <SessionWatcher onTick={this.handleSessionTick} />
        {!isRealExamPlayerRoute && (
          <div className="container-fluid">
            <Header
              fetchComments={this.props.fetchComments}
              fetchAllTasks={this.props.fetchAllTasks}
              fetchTimetables={this.props.fetchTimetables}
              isLoggedIn={Boolean(this.state.user && this.state.user.token)}
              user={this.state.user}
              childrenList={this.state.childrenList}
              onLogout={this.handleLogout}
              sessionRemainingMs={this.state.sessionRemainingMs}
            />
          </div>
        )}
        <div className={contentClassName} style={contentStyle}>
          <Suspense fallback={routeLoadingFallback}>
            <Routes>
              {/* Public pages stay routable for anonymous users, but only load when visited. */}
              <Route>
                <Route path="/register-parent" element={<ParentRegister onLoginSuccess={this.handleLoginSuccess} />} />
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                <Route path="/student-login" element={<StudentLogin />} />
                <Route
                  path="/login"
                  element={<LoginPage onLoginSuccess={this.handleLoginSuccess} />}
                />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/checkout-success" element={<SuccessPage />} />
                <Route path="/checkout-cancel" element={<CancelPage />} />
                <Route path="/" element={<LandingPage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/getting-started" element={<GettingStarted />} />
                <Route path="/session-expired" element={<SessionExpired />} />
                {/* Public on purpose: a blocked student must be able to reach it (STUD-631). */}
                <Route path="/subscription-required" element={<SubscriptionRequired />} />
              </Route>
            </Routes>
          </Suspense>
          {/* protected routes */}
          {isAuthenticated && (this.state.user?.admin || this.state.subscriptionActive) && (
            <Suspense fallback={routeLoadingFallback}>
              <Routes>

                {/* Guarded: the parent dashboard reports on a parent-selected child, so a
                    student typing this URL is sent to their own home instead. */}
                <Route
                  path="/parent-dashboard"
                  element={canViewParentDashboard(this.state.user?.role)
                    ? <ParentDashboard />
                    : <Navigate to={STUDENT_HOME_PATH} replace />}
                />
                <Route exact path="/TakeTests" element={<PracticeTest />} />
                <Route exact path="/take-tests" element={<PracticeTest />} />
                <Route path="/take-tests/real-exam/instructions" element={<RealExamInstructions />} />
                <Route path="/take-tests/real-exam/play" element={<RealExamPlayerPage />} />
                <Route path="/take-tests/:subject/topics" element={<TopicsView />} />
                <Route exact path="/QuestionManager" element={<QuestionManager />} />
                <Route exact path="/PromptManager" element={<PromptManager />} />
                {this.state.user?.admin && <Route exact path="/WritingPromptManager" element={<WritingPromptManager />} />}
                {this.state.user?.admin && <Route exact path="/PatternWizard" element={<PatternWizard />} />}
                {this.state.user?.admin && <Route exact path="/QuestionAuthoringStudio" element={<QuestionAuthoringStudio />} />}
                {this.state.user?.admin && <Route exact path="/pattern-details-report" element={<PatternDetailsReport />} />}
                {this.state.user?.admin && <Route exact path="/BatchQuestionUpload" element={<BatchQuestionUpload />} />}
                {(this.state.user?.admin || this.state.user?.role === 'parent' || this.state.user?.role === 'teacher') && (
                  <Route exact path="/remediation-dashboard" element={<StudentRemediationDashboard />} />
                )}
                <Route exact path="/CustomTestBuilder" element={<CustomTestBuilder studentId={STUDENT_ID} />} />
                <Route exact path="/finance" element={<Finance />} />
                <Route exact path="/loan-repayment-schedule" element={<LoanRepaymentSchedule />} />
                <Route exact path="/admin" element={<AdminDashboard />} />
                <Route path="/add-student" element={<AddStudentForm onAddStudent={this.handleStudentAdded} />} />
                <Route path="/results" element={<Results />} />
              </Routes>
            </Suspense>

          )}

        </div>
      </div>
    );
  }

}

// Exported unconnected for tests: the entitlement logic in refreshCurrentUserProfile is
// worth asserting directly, without standing up redux, the router and the lazy routes.
export { Home };

//connecting the component with redux store so that redux state updates reflect in the component prop
const ConnectedHome = connect(mapStateToProps, mapDispatchToProps)(Home);

function HomeWithLocation(props) {
  const location = useLocation();
  return <ConnectedHome {...props} location={location} />;
}

export default HomeWithLocation;
